import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useField, useFormikContext } from 'formik';
import useIntlMessage from '@app/hooks/use-intl-message';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './EditableSelect.scss';

const createOptionElement = (optionEl, key, onClick, selected, isPlaceholder) => (
  <div
    key={key}
    role="option"
    aria-selected={selected}
    aria-label={optionEl}
    tabIndex={-1}
    onClick={onClick}
    onKeyDown={e => e.key === 'Enter' && onClick()}
    className={classnames('options-list-el', { selected })}
    data-value={isPlaceholder ? '' : optionEl}
  >
    {optionEl}
  </div>
);

const EditableSelect = ({
  inputAriaLabelLocaleKey = '',
  selectAriaLabelLocaleKey = '',
  preselectFirst = false,
  editDisabled = false,
  selectPlaceholder = '',
  inputPlaceholder = '',
  preselectedItem = '',
  onChange = () => {},
  optionsList = [],
  field = {},
  ...props
}) => {
  const [optionsListExpanded, setOptionsListExpanded] = useState(false);
  const [selectElDimensions, setSelectElDimensions] = useState({});
  const [inputElDimensions, setInputElDimensions] = useState({});
  const [selected, setSelected] = useState();

  const { setFieldValue } = useFormikContext();
  const [formikField] = useField(field);
  const [inputAriaLabel, selectAriaLabel] = useIntlMessage(inputAriaLabelLocaleKey, selectAriaLabelLocaleKey);

  const { name: fieldName, value: fieldValue = '', ...formikFieldProps } = formikField;

  const hasMulltipleOptions = optionsList.length > 1;
  const editableInputsRef = useRef();
  const inputElRef = useRef();

  const closeOptionsList = ({ target }) => {
    const isSelectElementBody = target.classList?.contains('select-element');
    const isSelectElementContent = editableInputsRef.current.contains(target);

    if (isSelectElementBody || isSelectElementContent) return;

    setOptionsListExpanded(false);
  };

  const toggleOptionsList = e => {
    e.preventDefault();
    e.stopPropagation();
    setOptionsListExpanded(visible => !visible);
  };

  const onInputChange = e => {
    const {
      target: { value = '' },
    } = e;

    onChange?.(e);
    setFieldValue?.(fieldName, value);
  };

  const onSelectChange = e => {
    const { target: { dataset: { value = '' } } = {} } = e;

    inputElRef.current.value = value;
    e.target.value = value;
    setSelected(value);

    onChange?.(e);
    setFieldValue?.(fieldName, value);
  };

  const prefillEditableSelect = useCallback(() => {
    const defaultOption = preselectedItem && optionsList.find(option => preselectedItem === option);
    const selectedExist = selected && optionsList.find(option => selected === option);

    if (!selectedExist) {
      inputElRef.current.value = '';
      setSelected('');

      if (defaultOption) {
        inputElRef.current.value = defaultOption;
        setSelected(defaultOption);
      }

      if (!defaultOption && preselectFirst) {
        const [preselectedItem = ''] = optionsList;
        inputElRef.current.value = preselectedItem;
        setSelected(preselectedItem);
      }
    }
  }, [selected, optionsList, preselectedItem, preselectFirst, setSelected]);

  useEffect(() => {
    const { width: containerWidth, height: containerHeight } = editableInputsRef.current.getBoundingClientRect();
    const expandSelectBtnWidth = hasMulltipleOptions ? 18 : 0;
    const editableSelectPadding = 2;

    const commonHeight = Math.round(containerHeight - editableSelectPadding);
    const selectElWidth = Math.round(containerWidth - editableSelectPadding);
    const inputElWidth = Math.round(containerWidth - expandSelectBtnWidth - editableSelectPadding);

    setInputElDimensions({ height: commonHeight, width: inputElWidth });
    setSelectElDimensions({ height: commonHeight, width: selectElWidth });
  }, [optionsList, hasMulltipleOptions]);

  useEffect(() => {
    document.body.addEventListener('click', closeOptionsList);
    return () => document.body.removeEventListener('click', closeOptionsList);
  }, []);

  useEffect(() => {
    setFieldValue?.(fieldName, selected);
  }, [selected, setFieldValue, fieldName]);

  useEffect(prefillEditableSelect, [prefillEditableSelect, optionsList]);

  const createSelectElement = () => {
    const selectElementClassName = classnames('select-el', { expanded: optionsListExpanded });
    const selectOptionsClassName = classnames('select-options', { expanded: optionsListExpanded });
    const optionsListId = `${fieldName}-options`;
    return (
      <div
        className={selectElementClassName}
        style={selectElDimensions}
        aria-label={selectAriaLabel}
        aria-hidden={!editDisabled}
        tabIndex={editDisabled ? 0 : -1}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={optionsListExpanded}
        aria-controls={optionsListId}
        onClick={toggleOptionsList}
        onKeyDown={e => e.key === 'Enter' && toggleOptionsList()}
      >
        <div className={selectOptionsClassName} style={{ top: selectElDimensions.height }}>
          <div className="options-list-wrapper">
            <div className="options-list" id={optionsListId} role="listbox">
              {selectPlaceholder && createOptionElement(selectPlaceholder, null, onSelectChange, !selected, true)}
              {optionsList.map((optionEl, key) => createOptionElement(optionEl, key, onSelectChange, optionEl === selected))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const createInputElement = () => (
    <input
      type="editable-input"
      ref={inputElRef}
      name={fieldName}
      value={fieldValue}
      aria-label={inputAriaLabel}
      aria-disabled={editDisabled}
      aria-hidden={editDisabled}
      tabIndex={editDisabled ? -1 : 0}
      onChange={onInputChange}
      style={inputElDimensions}
      placeholder={inputPlaceholder || selectPlaceholder}
      disabled={editDisabled}
      {...formikFieldProps}
      maxLength="64"
    />
  );

  const createEditableSelect = () =>
    editDisabled ? (
      <>
        {createInputElement()}
        {hasMulltipleOptions && createSelectElement()}
      </>
    ) : (
      <>
        {hasMulltipleOptions && createSelectElement()}
        {createInputElement()}
      </>
    );

  return (
    <div {...props}>
      <div ref={editableInputsRef} className={classnames('editable-select-inputs', { 'input-only': !hasMulltipleOptions })}>
        {createEditableSelect()}
      </div>
    </div>
  );
};

EditableSelect.propTypes = {
  selectPlaceholder: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  preselectedItem: PropTypes.string,
  preselectFirst: PropTypes.bool,
  editDisabled: PropTypes.bool,
  optionsList: PropTypes.array,
  onChange: PropTypes.func,
  field: PropTypes.object,
};

export default EditableSelect;
